import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  ComplianceEngineGeneratorStatusBucket,
  WasteKind,
  WillNotReportSource,
} from "generated-graphql/graphql";
import {
  prettyPrintWasteClassification,
  prettyPrintWasteProgram,
} from "routes/Customer/Waste/Facilities/Facility/prettyPrintWasteClassification";

import { ExplainedField, ExplanationResult } from "./types";

const WILL_NOT_REPORT_SOURCE_LABEL: Record<WillNotReportSource, string> = {
  [WillNotReportSource.Derived]: "derived",
  [WillNotReportSource.DerivedSubpartP]: "derived, Subpart P",
  [WillNotReportSource.DerivedUniversalWaste]: "derived, universal waste",
  [WillNotReportSource.Manual]: "manual override",
  [WillNotReportSource.Vendor]: "vendor data",
};

/** A human phrase for `willNotReportSource`. Null when the engine didn't name one. */
export function willNotReportSourceLabel(
  source: WillNotReportSource | null | undefined
): string | null {
  return source ? WILL_NOT_REPORT_SOURCE_LABEL[source] : null;
}

/** "Universal Waste", "Used Oil", "Pharmaceutical" — or the plain enum ("RCRA") when there's no special program to name. */
export function wasteKindConclusionLabel(wasteKind: ExplanationResult["wasteKind"]): string {
  return prettyPrintWasteProgram(wasteKind) || prettyPrintEnumValue(wasteKind);
}

/**
 * The overall reportability verdict with the engine's own provenance in
 * parentheses, e.g. "Not reportable (derived, Subpart P)". The biennial and
 * e-Manifest answers behind it are their own conclusions, not a second clause
 * crammed onto this one.
 */
export function reportabilityValue(
  result: Pick<ExplanationResult, "willNotReport" | "willNotReportSource">
): string {
  const headline = result.willNotReport ? "Not reportable" : "Reportable";
  const source = willNotReportSourceLabel(result.willNotReportSource);
  return source ? `${headline} (${source})` : headline;
}

const BUCKET_LABEL: Record<ComplianceEngineGeneratorStatusBucket, string> = {
  [ComplianceEngineGeneratorStatusBucket.Acute]: "Yes (acute)",
  [ComplianceEngineGeneratorStatusBucket.NonAcute]: "Yes (non-acute)",
};

/**
 * Answers the question the field label asks — "counts toward generator
 * status?" — with yes or no, naming the bucket only as a qualifier on a yes.
 */
function bucketLabel(bucket: ExplanationResult["countsTowardGeneratorStatus"]["federal"]): string {
  return bucket ? BUCKET_LABEL[bucket] : "No";
}

/**
 * The biennial verdict paired with the resulting overall not-reportable state
 * and, when the engine named one, its provenance. Carries the same three facts
 * as the engine's raw `finalValue` for this field, in plain words.
 */
function reportableOnFederalBiennialValue(result: ExplanationResult): string {
  const biennial = result.reportableOnFederalBiennial ? "Reportable" : "Not reportable";
  const overall = result.willNotReport ? "will not report overall" : "will report overall";
  const source = willNotReportSourceLabel(result.willNotReportSource);
  return source ? `${biennial} · ${overall} (${source})` : `${biennial} · ${overall}`;
}

export type ExplainedFieldSpec = {
  label: string;
  /** The rules' answer for this field, in the same words the conclusions card uses. */
  value: (result: ExplanationResult) => string;
  /**
   * What the card says when no rule fired. These sentences are the portal's,
   * not the engine's: the engine emits a reason only for a rule that fired,
   * and a field with no match keeps its starting value silently.
   */
  defaultSentence: (result: ExplanationResult) => string;
};

const constant = (sentence: string) => () => sentence;

export const EXPLAINED_FIELDS: Record<ExplainedField, ExplainedFieldSpec> = {
  classification: {
    label: "Classification",
    value: (result) => prettyPrintWasteClassification(result.classification),
    defaultSentence: constant(
      "No rule matched that would mark this line as hazardous or state-regulated waste, so it is non-hazardous."
    ),
  },
  reportableOnFederalBiennial: {
    label: "Reportable on the federal biennial report",
    value: reportableOnFederalBiennialValue,
    defaultSentence: constant(
      "No rule matched, so the line is on the federal biennial report by default."
    ),
  },
  subjectToEManifest: {
    label: "Subject to federal e-Manifest",
    value: (result) => (result.subjectToEManifest ? "Yes" : "No"),
    defaultSentence: constant(
      "No rule matched, so the line is not subject to e-Manifest by default."
    ),
  },
  countsTowardGeneratorStatusFederal: {
    label: "Counts toward federal generator status",
    value: (result) => bucketLabel(result.countsTowardGeneratorStatus.federal),
    defaultSentence: constant(
      "No rule matched, so the line does not count toward federal generator status by default."
    ),
  },
  countsTowardGeneratorStatusState: {
    label: "Counts toward state generator status",
    value: (result) => bucketLabel(result.countsTowardGeneratorStatus.state),
    defaultSentence: constant(
      "No rule matched, so the line does not count toward state generator status by default."
    ),
  },
  wasteKind: {
    label: "Waste kind",
    value: (result) => wasteKindConclusionLabel(result.wasteKind),
    // The kind follows the grant, so its reason lives on the category card.
    defaultSentence: (result) =>
      result.wasteKind === WasteKind.UniversalWaste
        ? "A rule granted a universal waste category, which makes the kind universal waste. The rule is listed under Universal waste category."
        : "No rule matched that would mark this line as universal waste, pharmaceutical or used oil, so it is RCRA waste.",
  },
  universalWasteGrant: {
    label: "Universal waste category",
    value: (result) =>
      result.universalWasteCategory ? prettyPrintEnumValue(result.universalWasteCategory) : "None",
    defaultSentence: constant("No rule granted a universal waste category, so it has none."),
  },
};

export type ExplainedSection = {
  key: string;
  label: string;
  fields: readonly ExplainedField[];
  /** Heading between the first card and the rest, when the first is the verdict the others qualify. */
  siblingsHeading?: string;
};

/** The portal's grouping of explained fields into sections, in display order. */
export const EXPLAINED_SECTIONS: readonly ExplainedSection[] = [
  { key: "classification", label: "Waste classification", fields: ["classification"] },
  {
    key: "applicability",
    label: "Reportability and generator status",
    siblingsHeading: "What else applied",
    fields: [
      "reportableOnFederalBiennial",
      "subjectToEManifest",
      "countsTowardGeneratorStatusFederal",
      "countsTowardGeneratorStatusState",
    ],
  },
  {
    key: "wasteKind",
    label: "Waste kind and universal waste",
    fields: ["wasteKind", "universalWasteGrant"],
  },
];
